// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file DateInput.tsx
 * @input Uses React, useId, useState, useCallback, useRef, Field, Icon, Calendar, usePopover, InputGroupContext
 * @output Exports DateInput component, DateInputProps
 * @position Core implementation; consumed by index.ts, tested by DateInput.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/DateInput/DateInput.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/DateInput/DateInput.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/DateInput/index.ts (exports if types change)
 * - /apps/storybook/stories/DateInput.stories.tsx (storybook stories)
 * - /packages/cli/assets/templates/blocks/components/DateInput/ (showcase blocks)
 */
import { useId, useState, useCallback, useRef, useOptimistic, useTransition } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, sizeVars, radiusVars, typographyVars, typeScaleVars, borderVars } from "../theme/tokens.stylex.js";
import { Field, inputWrapperStyles, inputStatusBorderStyles, inputStatusHoverShadowStyles, inputStatusFocusWithinStyles } from "../Field/index.js";
import { Icon } from "../Icon/index.js";
import { VisuallyHidden } from "../VisuallyHidden/index.js";
import { useInputGroup } from "../InputGroup/InputGroupContext.js";
import { groupStyles } from "../InputGroup/groupStyles.js";
import { useSize } from "../SizeContext/SizeContext.js";
import { Spinner } from "../Spinner/index.js";
import { Calendar } from "../Calendar/index.js";
import { useCalendarConstraints } from "../Calendar/hooks/index.js";
import { useInputStatusIcon } from "../hooks/useInputStatusIcon.js";
import { usePopover } from "../Popover/index.js";
import { useTooltip } from "../Tooltip/index.js";
import { getInputARIA, parseDateInput } from "../utils/index.js";
import { plainDateFromISO, plainDateToISO, formatSharedDate } from "../utils/plainDate.js";
const sizeStyles = {
  sm: {
    kZKoxP: "x6k0iem",
    k7Eaqz: "xfb3i0g",
    $$css: true
  },
  md: {
    kZKoxP: "x1ueg155",
    k7Eaqz: "xfb3i0g",
    $$css: true
  },
  lg: {
    kZKoxP: "xssyfek",
    k7Eaqz: "xfb3i0g",
    $$css: true
  }
};

/**
 * Named display formats for a committed date value. These are the date-only
 * members of Timestamp's `format` vocabulary — reused verbatim (via
 * `Extract`) so the same literal renders the same date shape in both
 * `Timestamp` and `DateInput`:
 * - `'date'`: locale short-month date, e.g. "Mar 21, 2026"
 * - `'date_long'`: locale long-month date, e.g. "March 21, 2026" (the default)
 * - `'date_weekday'`: short weekday + date, e.g. "Wed, Mar 21, 2026"
 * - `'system_date'`: ISO 8601 calendar date, e.g. "2026-03-21"
 *
 * Because `DateInputFormat` is `Extract`ed from `TimestampFormat`, the two
 * types stay in compile-time lockstep: renaming or removing one of these
 * members from `TimestampFormat` breaks this type at build time.
 */

// Re-export shared types for convenience

import { mergeProps, mergeRefs } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * A date picker component combining a text input with a calendar popover.
 *
 * @example
 * ```
 * <DateInput
 *   label="Event date"
 *   value={date}
 *   onChange={setDate}
 * />
 * ```
 */
export function DateInput({
  label,
  isLabelHidden = false,
  description,
  isOptional = false,
  isRequired = false,
  isDisabled = false,
  disabledMessage,
  value,
  onChange,
  changeAction,
  isLoading = false,
  min,
  max,
  dateConstraints,
  placeholder: placeholderFromProps,
  size: sizeProp,
  status,
  statusVariant = 'attached',
  labelTooltip,
  hasClear = false,
  numberOfMonths = 1,
  weekStartsOn,
  format = 'date_long',
  width,
  xstyle,
  className,
  style,
  ref,
  ...rest
}) {
  const t = useTranslator();
  const placeholder = placeholderFromProps ?? t('@astryx.dateInput.placeholder');
  const size = useSize(sizeProp, 'md');
  const id = useId();
  const inputLabelID = useId();
  const descriptionID = useId();
  const statusMessageID = useId();
  const inputRef = useRef(null);
  const calendarRef = useRef(null);
  const lastFiredValueRef = useRef(undefined);
  const inputGroup = useInputGroup();
  const [, startTransition] = useTransition();
  const [optimisticValue, setOptimisticValue] = useOptimistic(value);
  const isBusy = isLoading || optimisticValue !== value;
  const isEffectivelyDisabled = isDisabled || isBusy;

  // Disabled-reason tooltip. Disabled controls swallow pointer events, so the
  // tooltip listeners attach to the input container (which already exists) and
  // the text input stays perceivable via aria-disabled instead of the disabled
  // attribute. Typing is blocked with readOnly and value mutation guards;
  // calendar activation is blocked by the isEffectivelyDisabled guards. Only
  // the persistent isDisabled state (not the transient busy state) surfaces a
  // reason.
  const showsDisabledMessage = isDisabled && !!disabledMessage;
  const disabledMessageTooltip = useTooltip({
    placement: 'above',
    // The container div is not naturally focusable; focusin bubbles up from
    // the input, so always attach focus listeners.
    focusTrigger: 'always',
    isEnabled: showsDisabledMessage
  });

  // Constraint checking for text input validation (reuses calendar logic)
  const {
    isDateDisabled
  } = useCalendarConstraints({
    min,
    max,
    dateConstraints
  });
  const {
    statusIcon,
    describedBy: statusTooltipDescribedBy
  } = useInputStatusIcon({
    status,
    statusVariant,
    isInGroup: !!inputGroup
  });
  const {
    ariaLabelledBy,
    ariaDescribedBy
  } = getInputARIA(inputLabelID, [description ? descriptionID : null, statusVariant !== 'tooltip' && status?.message ? statusMessageID : null,
  // The tooltip variant renders no message box; describe the input by the
  // tooltip's content instead so the status is still announced.
  statusTooltipDescribedBy, showsDisabledMessage ? disabledMessageTooltip.describedBy : null], inputGroup);

  // Pending input while user is typing (null = show formatted value)
  const [pendingInput, setPendingInput] = useState(null);

  // Clear pending input when value changes externally (computed during render
  // via prev-value ref instead of useEffect to avoid an extra render cycle)
  const prevValueRef = useRef(value);
  if (value !== prevValueRef.current) {
    prevValueRef.current = value;
    if (value !== lastFiredValueRef.current) {
      lastFiredValueRef.current = undefined;
      if (pendingInput !== null) {
        setPendingInput(null);
      }
    }
  }

  // Format a committed ISO value for display. The default `date_long` renders
  // the long-month shape (byte-identical to the historical hardcoded
  // DATE_FORMAT_LONG rendering, so still non-breaking); a function is called
  // with the ISO value; every other named member reuses Timestamp's shared
  // date mapping. Applies ONLY to the committed value, never to in-progress
  // typed input.
  const formatCommittedValue = useCallback(iso => typeof format === 'function' ? format(iso) : formatSharedDate(plainDateFromISO(iso), format), [format]);

  // Display value: pending input if typing, otherwise formatted value
  const displayValue = pendingInput !== null ? pendingInput : optimisticValue && /^\d{4}-\d{2}-\d{2}$/.test(optimisticValue) ? formatCommittedValue(optimisticValue) : '';

  // Check if current input is valid (for styling purposes)
  const isInputValid = pendingInput === null || !pendingInput.trim() ? true : parseDateInput(pendingInput) !== null;
  const popover = usePopover({
    dialogLabel: t('@astryx.dateInput.dialogLabel'),
    closeButtonLabel: t('@astryx.dateInput.closeCalendar'),
    onHide: () => inputRef.current?.focus()
  });

  // Handle toggling the popover from button click (focus calendar)
  const handleToggle = useCallback(() => {
    if (!isEffectivelyDisabled) {
      if (popover.isOpen) {
        popover.hide();
      } else {
        popover.show();
      }
    }
  }, [isEffectivelyDisabled, popover]);

  // Handle opening the popover from input click (keep focus in input)
  const handleInputClick = useCallback(() => {
    if (!isEffectivelyDisabled && !popover.isOpen) {
      popover.show({
        skipAutoFocus: true
      });
    }
  }, [isEffectivelyDisabled, popover]);

  // Unified change handler that fires both onChange and changeAction
  const fireChange = useCallback(newValue => {
    if (isBusy) {
      return;
    }
    onChange?.(newValue);
    if (changeAction) {
      startTransition(async () => {
        setOptimisticValue(newValue);
        await changeAction(newValue);
      });
    }
  }, [isBusy, onChange, changeAction, startTransition, setOptimisticValue]);

  // Handle clear button click
  const handleClear = useCallback(() => {
    fireChange(undefined);
    inputRef.current?.focus();
  }, [fireChange]);

  // Handle date selection from calendar
  const handleDateSelect = useCallback(selectedDate => {
    fireChange(selectedDate);
    setPendingInput(null);
    popover.hide();
  }, [fireChange, popover]);

  // Handle input text change - update immediately if valid and allowed
  const handleInputChange = useCallback(e => {
    // With a disabledMessage the input drops `disabled` for focusability, so
    // guard value mutation explicitly (readOnly also blocks typing).
    if (isEffectivelyDisabled) {
      return;
    }
    const newValue = e.target.value;
    setPendingInput(newValue);

    // If the input is valid and passes constraints, update immediately
    const parsed = parseDateInput(newValue);
    if (parsed && plainDateToISO(parsed) !== value && !isDateDisabled(parsed)) {
      const parsedISO = plainDateToISO(parsed);
      lastFiredValueRef.current = parsedISO;
      fireChange(parsedISO);
      // Navigate calendar to show the parsed date's month
      calendarRef.current?.navigateTo(parsedISO);
    }
  }, [value, fireChange, isDateDisabled, isEffectivelyDisabled]);

  // Commit pending input (shared by blur and Enter key)
  const commitPendingInput = useCallback(() => {
    if (pendingInput === null) {
      return;
    }
    if (!pendingInput.trim()) {
      if (value !== undefined) {
        fireChange(undefined);
      }
      setPendingInput(null);
      return;
    }
    const parsed = parseDateInput(pendingInput);
    if (parsed && !isDateDisabled(parsed)) {
      const parsedISO = plainDateToISO(parsed);
      if (parsedISO !== value) {
        fireChange(parsedISO);
      }
    }
    setPendingInput(null);
  }, [pendingInput, value, fireChange, isDateDisabled]);

  // Handle blur - validate, check constraints, and clear pending input
  const handleBlur = useCallback(() => {
    commitPendingInput();
  }, [commitPendingInput]);

  // Handle keyboard events on input
  const handleInputKeyDown = useCallback(e => {
    if (e.key === 'Escape' && popover.isOpen) {
      e.preventDefault();
      popover.hide();
    } else if ((e.key === 'ArrowDown' || e.altKey && e.key === 'ArrowDown') && !popover.isOpen) {
      // APG combobox: ArrowDown (and Alt+ArrowDown) opens the calendar
      // popover from the keyboard, keeping focus in the input (forms-13).
      e.preventDefault();
      if (!isEffectivelyDisabled) {
        popover.show({
          skipAutoFocus: true
        });
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commitPendingInput();
    }
  }, [popover, commitPendingInput, isEffectivelyDisabled]);
  const inputWrapper = /*#__PURE__*/_jsxs("div", {
    ref: el => {
      popover.triggerRef(el);
      // Anchor + hover/focus listeners for the disabled-message tooltip.
      // Handlers are gated internally by isEnabled, and anchor names
      // compose, so attaching unconditionally is safe.
      disabledMessageTooltip.ref(el);
    },
    ...rest,
    ...mergeProps(themeProps('date-input', {
      size,
      status: status?.type ?? null,
      disabled: isDisabled ? 'disabled' : null
    }), stylex.props(inputWrapperStyles.base, sizeStyles[size], isEffectivelyDisabled && inputWrapperStyles.disabled, status && inputStatusBorderStyles[status.type], status && !isEffectivelyDisabled && inputStatusHoverShadowStyles[status.type], status && inputStatusFocusWithinStyles[status.type], inputGroup && groupStyles.inGroup, xstyle), className, style),
    children: [inputGroup && /*#__PURE__*/_jsx(VisuallyHidden, {
      id: inputLabelID,
      children: label
    }), /*#__PURE__*/_jsx("button", {
      type: "button",
      onClick: handleToggle,
      disabled: isEffectivelyDisabled,
      "aria-label": popover.isOpen ? t('@astryx.dateInput.toggleCalendarClose') : t('@astryx.dateInput.openCalendar'),
      ...{
        0: {
          className: "x78zum5 x6s0dn4 xl56j7k x1717udv x1ghz6dp xc342km xng3xce xjbqb8w x1ypdohk xh6dtrn x1a2a7pz x1p25gnr x1y3gkto"
        },
        1: {
          className: "x78zum5 x6s0dn4 xl56j7k x1717udv x1ghz6dp xc342km xng3xce xjbqb8w xh6dtrn x1a2a7pz x1p25gnr x1y3gkto x1h6gzvc"
        }
      }[!!isEffectivelyDisabled << 0],
      children: /*#__PURE__*/_jsx(Icon, {
        icon: "calendar",
        size: "sm",
        color: "secondary"
        // Stable theme target on the toggle glyph itself, so a theme can
        // restyle just this icon (color, size, hover) — and each open/closed
        // state — via `defineTheme`. Same-element rules in @layer astryx-theme
        // win over the icon's own base color/size, which a button-level target
        // could not reach. Reflects the popover's open/closed state as a
        // `data-state` attribute.
        ,
        ...themeProps('date-input-toggle-icon', {
          state: popover.isOpen ? 'expanded' : 'collapsed'
        })
      })
    }), /*#__PURE__*/_jsx("input", {
      ref: mergeRefs(ref, inputRef),
      id: id,
      type: "text",
      role: "combobox",
      value: displayValue,
      onChange: handleInputChange,
      onBlur: handleBlur,
      onClick: handleInputClick,
      onKeyDown: handleInputKeyDown,
      placeholder: placeholder
      // With a disabledMessage the input keeps focusability via
      // aria-disabled so the reason is focus-discoverable; typing is
      // blocked with readOnly and the mutation guards, and calendar
      // activation is blocked by the isEffectivelyDisabled guards.
      ,
      disabled: isEffectivelyDisabled && !showsDisabledMessage,
      "aria-disabled": showsDisabledMessage ? 'true' : undefined,
      readOnly: showsDisabledMessage || undefined,
      "aria-labelledby": ariaLabelledBy,
      "aria-describedby": ariaDescribedBy,
      "aria-required": isRequired === true ? 'true' : undefined,
      "aria-invalid": status?.type === 'error' || !isInputValid ? 'true' : undefined,
      "aria-busy": isBusy || undefined,
      "aria-expanded": popover.isOpen,
      "aria-haspopup": "dialog",
      "aria-controls": popover.isOpen ? popover.id : undefined,
      "aria-autocomplete": "none",
      autoComplete: "off",
      ...{
        0: {
          className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx x1tgivj0 xjbqb8w x1a2a7pz xeyghm5"
        },
        2: {
          className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx x1tgivj0 xjbqb8w x1a2a7pz xeyghm5 x1h6gzvc"
        },
        1: {
          className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx xjbqb8w x1a2a7pz xeyghm5 xv1l7n4"
        },
        3: {
          className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx xjbqb8w x1a2a7pz xeyghm5 x1h6gzvc xv1l7n4"
        }
      }[!!isEffectivelyDisabled << 1 | !!!isInputValid << 0]
    }), /*#__PURE__*/_jsx(VisuallyHidden, {
      as: "div",
      role: "alert",
      "aria-live": "assertive",
      children: !isInputValid ? 'Invalid date' : ''
    }), hasClear && value !== undefined && !isEffectivelyDisabled && /*#__PURE__*/_jsx("button", {
      type: "button",
      onClick: handleClear,
      "aria-label": t('@astryx.dateInput.clear', {
        label
      }),
      ...{
        className: "x78zum5 x6s0dn4 xl56j7k x1717udv x1ghz6dp xc342km xng3xce xjbqb8w x1ypdohk xh6dtrn x1a2a7pz x1p25gnr x1y3gkto"
      },
      children: /*#__PURE__*/_jsx(Icon, {
        icon: "close",
        size: "sm",
        color: "secondary"
        // Stable theme target on the clear glyph itself, so a theme can
        // restyle just this icon (color, size, hover) via `defineTheme`.
        // Same-element rules in @layer astryx-theme win over the icon's own
        // base color/size, which a button-level target could not reach.
        ,
        ...themeProps('date-input-clear-icon')
      })
    }), isBusy && /*#__PURE__*/_jsx(Spinner, {
      size: "sm"
    }), statusIcon, popover.render(/*#__PURE__*/_jsx(Calendar, {
      handleRef: calendarRef,
      mode: "single",
      value: optimisticValue,
      onChange: handleDateSelect,
      min: min,
      max: max,
      dateConstraints: dateConstraints,
      numberOfMonths: numberOfMonths,
      weekStartsOn: weekStartsOn
    }), {
      placement: 'below',
      alignment: 'start'
    }), showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
  });
  if (inputGroup) {
    return inputWrapper;
  }
  return /*#__PURE__*/_jsx(Field, {
    label: label,
    isLabelHidden: isLabelHidden,
    description: description,
    inputID: id,
    descriptionID: description ? descriptionID : undefined,
    isOptional: isOptional,
    isRequired: isRequired,
    isDisabled: isDisabled,
    status: status ? {
      type: status.type,
      message: status.message,
      messageID: status.message ? statusMessageID : undefined
    } : undefined,
    statusVariant: statusVariant,
    labelTooltip: labelTooltip,
    width: width,
    children: inputWrapper
  });
}
DateInput.displayName = 'DateInput';